import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/data/site";
import Logo from "@/components/Logo";
import { useQuoteModal } from "@/context/QuoteModalContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-slate-200 bg-white/85 backdrop-blur-xl"
          : "border-transparent bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Logo />

        <nav
          data-testid="primary-nav"
          className="hidden items-center gap-1 lg:flex"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-cyan-700"
                    : "text-slate-700 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex items-center gap-1.5">
                  {link.label}
                  {isActive && (
                    <span className="ml-1 h-1 w-1 rounded-full bg-cyan-600" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button
            data-testid="header-get-quote-btn"
            onClick={() => openModal()}
            className="rounded-md bg-cyan-600 px-4 py-2 font-medium text-white hover:bg-cyan-700"
          >
            Get a Quote
            <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((s) => !s)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:border-cyan-600/70 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-8">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-cyan-600/10 text-cyan-700"
                      : "text-slate-700 hover:bg-white/5 hover:text-slate-900"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <button
              type="button"
              data-testid="mobile-get-quote-btn"
              onClick={() => {
                setOpen(false);
                openModal();
              }}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700"
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
